/**
 * GDPR Controls CAT Catalog
 * Controls derived from GDPR articles (Lawfulness, Rights, Art.32, DPIA, Breach, Processors, Accountability)
 */

export type GdpControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';
export type GdpDomain =
  | 'Lawfulness & Transparency'
  | 'Data Subject Rights'
  | 'Security of Processing'
  | 'DPIA & Risk'
  | 'Breach Notification'
  | 'Processors & Supply Chain'
  | 'Accountability & Governance';

export interface GdpControl {
  standard: string;
  controlCode: string;
  domain: GdpDomain;
  title: { en: string; es: string; ru: string };
  description: { en: string; es: string; ru: string };
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE';
  evidence: { type: string; examples: { en: string[]; es: string[]; ru: string[] } }[];
  vendorImpact: { applies: boolean };
  governanceLinks: { policies: string[]; processes: string[]; roles: string[] };
  crossMapping: { ISO27001: string[]; ENS_ES: string[]; SOC2: string[] };
  recommendations: {
    CRITICAL?: { en: string; es: string; ru: string };
    MAJOR?: { en: string; es: string; ru: string };
    MODERATE?: { en: string; es: string; ru: string };
  };
}

const ev = (type: string, en: string[], es: string[], ru: string[]) => ({
  type,
  examples: { en, es, ru },
});

const def = (
  code: string,
  domain: GdpDomain,
  title: { en: string; es: string; ru: string },
  desc: { en: string; es: string; ru: string },
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE',
  evidence: GdpControl['evidence'],
  vendor: boolean,
  rec: Partial<GdpControl['recommendations']>,
  cross: Partial<GdpControl['crossMapping']> = {}
): GdpControl => ({
  standard: 'GDPR',
  controlCode: `GDPR_${code}`,
  domain,
  title,
  description: desc,
  severity,
  evidence,
  vendorImpact: { applies: vendor },
  governanceLinks: { policies: [], processes: [], roles: [] },
  crossMapping: { ISO27001: [], ENS_ES: [], SOC2: [], ...cross },
  recommendations: rec,
});

// Lawfulness & Transparency (Art.5, 6, 7, 13-14)
const GDPR_LAWFULNESS: GdpControl[] = [
  def(
    'LAW_01',
    'Lawfulness & Transparency',
    { en: 'Legal basis for processing', es: 'Base legal para el tratamiento', ru: 'Правовое основание обработки' },
    { en: 'Legal grounds are defined and documented for each processing activity.', es: 'Las bases legales están definidas y documentadas para cada actividad de tratamiento.', ru: 'Правовые основания определены и задокументированы для каждой операции обработки.' },
    'CRITICAL',
    [ev('record', ['Records of processing activities', 'Legal basis register'], ['Registro de actividades', 'Registro de bases legales'], ['Реестр операций', 'Реестр правовых оснований'])],
    false,
    { CRITICAL: { en: 'Document legal basis for all processing operations.', es: 'Documentar la base legal para todas las operaciones de tratamiento.', ru: 'Задокументировать правовые основания для всех операций обработки.' } },
    { ISO27001: ['A.5.1'], SOC2: ['CC1'] }
  ),
  def(
    'LAW_02',
    'Lawfulness & Transparency',
    { en: 'Privacy notice and transparency', es: 'Aviso de privacidad y transparencia', ru: 'Уведомление о конфиденциальности' },
    { en: 'Data subjects receive clear information about processing purposes and their rights.', es: 'Los interesados reciben información clara sobre fines y derechos.', ru: 'Субъекты данных получают ясную информацию о целях и правах.' },
    'CRITICAL',
    [ev('policy', ['Privacy notice', 'Information notice template'], ['Aviso de privacidad', 'Plantilla de información'], ['Политика конфиденциальности', 'Шаблон уведомления'])],
    false,
    { CRITICAL: { en: 'Publish and maintain accurate privacy notices.', es: 'Publicar y mantener avisos de privacidad precisos.', ru: 'Публиковать и поддерживать точные уведомления о конфиденциальности.' } }
  ),
  def(
    'LAW_03',
    'Lawfulness & Transparency',
    { en: 'Consent management', es: 'Gestión del consentimiento', ru: 'Управление согласиями' },
    { en: 'Consent is obtained, documented and can be withdrawn.', es: 'El consentimiento se obtiene, documenta y puede retirarse.', ru: 'Согласие получается, документируется и может быть отозвано.' },
    'CRITICAL',
    [ev('record', ['Consent records', 'Withdrawal procedure'], ['Registros de consentimiento', 'Procedimiento de retiro'], ['Реестры согласий', 'Процедура отзыва'])],
    false,
    { CRITICAL: { en: 'Implement consent management with documentation and withdrawal capability.', es: 'Implementar gestión de consentimiento con documentación y capacidad de retiro.', ru: 'Внедрить управление согласиями с документированием и возможностью отзыва.' } }
  ),
];

// Data Subject Rights (Art.12-23)
const GDPR_RIGHTS: GdpControl[] = [
  def(
    'RIGHTS_01',
    'Data Subject Rights',
    { en: 'Right of access procedures', es: 'Procedimientos de derecho de acceso', ru: 'Процедуры права на доступ' },
    { en: 'Procedures exist for handling access requests within 30 days.', es: 'Existen procedimientos para solicitudes de acceso en 30 días.', ru: 'Существуют процедуры обработки запросов на доступ в течение 30 дней.' },
    'CRITICAL',
    [ev('procedure', ['DSAR procedure', 'Response SLA'], ['Procedimiento DSAR', 'SLA de respuesta'], ['Процедура DSAR', 'SLA ответа'])],
    false,
    { CRITICAL: { en: 'Define and implement DSAR procedures with 30-day SLA.', es: 'Definir e implementar procedimientos DSAR con SLA de 30 días.', ru: 'Определить и внедрить процедуры DSAR со сроком ответа 30 дней.' } }
  ),
  def(
    'RIGHTS_02',
    'Data Subject Rights',
    { en: 'Right to erasure and rectification', es: 'Derecho al borrado y rectificación', ru: 'Право на удаление и исправление' },
    { en: 'Procedures for erasure, rectification and restriction of processing.', es: 'Procedimientos para borrado, rectificación y limitación.', ru: 'Процедуры удаления, исправления и ограничения обработки.' },
    'CRITICAL',
    [ev('procedure', ['Erasure procedure', 'Rectification workflow'], ['Procedimiento de borrado', 'Flujo de rectificación'], ['Процедура удаления', 'Процесс исправления'])],
    false,
    { CRITICAL: { en: 'Implement procedures for rights to erasure and rectification.', es: 'Implementar procedimientos para derechos al borrado y rectificación.', ru: 'Внедрить процедуры права на удаление и исправление.' } }
  ),
  def(
    'RIGHTS_03',
    'Data Subject Rights',
    { en: 'Right to data portability', es: 'Derecho a la portabilidad', ru: 'Право на переносимость' },
    { en: 'Data portability requests can be fulfilled in machine-readable format.', es: 'Las solicitudes de portabilidad pueden cumplirse en formato legible.', ru: 'Запросы на переносимость могут быть выполнены в машиночитаемом формате.' },
    'MAJOR',
    [ev('procedure', ['Portability procedure', 'Export format'], ['Procedimiento de portabilidad', 'Formato de exportación'], ['Процедура переносимости', 'Формат экспорта'])],
    false,
    { MAJOR: { en: 'Establish data portability procedure and export capabilities.', es: 'Establecer procedimiento de portabilidad y capacidades de exportación.', ru: 'Установить процедуру переносимости и возможности экспорта.' } }
  ),
];

// Security of Processing (Art.32)
const GDPR_SECURITY: GdpControl[] = [
  def(
    'SEC_01',
    'Security of Processing',
    { en: 'Encryption and pseudonymization', es: 'Cifrado y seudonimización', ru: 'Шифрование и псевдонимизация' },
    { en: 'Appropriate technical measures including encryption and pseudonymization.', es: 'Medidas técnicas apropiadas incluyendo cifrado y seudonimización.', ru: 'Соответствующие технические меры, включая шифрование и псевдонимизацию.' },
    'CRITICAL',
    [ev('record', ['Encryption config', 'Pseudonymization docs'], ['Configuración de cifrado', 'Docs de seudonimización'], ['Настройки шифрования', 'Документация псевдонимизации'])],
    false,
    { CRITICAL: { en: 'Implement encryption for sensitive data at rest and in transit.', es: 'Implementar cifrado para datos sensibles en reposo y tránsito.', ru: 'Внедрить шифрование чувствительных данных при хранении и передаче.' } },
    { ISO27001: ['A.8.24'], SOC2: ['CC6'] }
  ),
  def(
    'SEC_02',
    'Security of Processing',
    { en: 'Access control and RBAC', es: 'Control de acceso y RBAC', ru: 'Контроль доступа и RBAC' },
    { en: 'Access control ensures only authorized access to personal data.', es: 'El control de acceso asegura solo acceso autorizado a datos personales.', ru: 'Контроль доступа обеспечивает только санкционированный доступ к персональным данным.' },
    'CRITICAL',
    [ev('procedure', ['Access control policy', 'RBAC matrix'], ['Política de acceso', 'Matriz RBAC'], ['Политика доступа', 'Матрица RBAC'])],
    false,
    { CRITICAL: { en: 'Implement RBAC and least-privilege access for personal data.', es: 'Implementar RBAC y acceso de menor privilegio.', ru: 'Внедрить RBAC и принцип наименьших привилегий.' } }
  ),
  def(
    'SEC_03',
    'Security of Processing',
    { en: 'Resilience and backup', es: 'Resiliencia y copias de seguridad', ru: 'Устойчивость и резервное копирование' },
    { en: 'Ability to restore availability after incident; backup and recovery.', es: 'Capacidad de restaurar disponibilidad tras incidente; backup y recuperación.', ru: 'Возможность восстановления после инцидента; резервное копирование.' },
    'MAJOR',
    [ev('record', ['BCP', 'Backup test logs'], ['BCP', 'Registros de pruebas de backup'], ['План восстановления', 'Логи тестов резервирования'])],
    false,
    { MAJOR: { en: 'Define backup and recovery procedures; test regularly.', es: 'Definir procedimientos de backup y recuperación; probar regularmente.', ru: 'Определить процедуры резервирования и восстановления; тестировать регулярно.' } }
  ),
];

// DPIA & Risk (Art.35)
const GDPR_DPIA: GdpControl[] = [
  def(
    'DPIA_01',
    'DPIA & Risk',
    { en: 'DPIA methodology and process', es: 'Metodología y proceso DPIA', ru: 'Методология и процесс DPIA' },
    { en: 'DPIA is conducted for high-risk processing; methodology documented.', es: 'Se realiza DPIA para tratamiento de alto riesgo; metodología documentada.', ru: 'DPIA проводится для высокорисковой обработки; методология документирована.' },
    'CRITICAL',
    [ev('procedure', ['DPIA procedure', 'DPIA template'], ['Procedimiento DPIA', 'Plantilla DPIA'], ['Процедура DPIA', 'Шаблон DPIA'])],
    false,
    { CRITICAL: { en: 'Implement DPIA process for high-risk processing activities.', es: 'Implementar proceso DPIA para actividades de alto riesgo.', ru: 'Внедрить процесс DPIA для высокорисковых операций обработки.' } }
  ),
  def(
    'DPIA_02',
    'DPIA & Risk',
    { en: 'DPIA register and review', es: 'Registro DPIA y revisión', ru: 'Реестр DPIA и пересмотр' },
    { en: 'DPIA outcomes are registered and reviewed periodically.', es: 'Los resultados DPIA se registran y revisan periódicamente.', ru: 'Результаты DPIA регистрируются и пересматриваются периодически.' },
    'MAJOR',
    [ev('record', ['DPIA register', 'Review log'], ['Registro DPIA', 'Log de revisión'], ['Реестр DPIA', 'Журнал пересмотра'])],
    false,
    { MAJOR: { en: 'Maintain DPIA register and schedule periodic reviews.', es: 'Mantener registro DPIA y programar revisiones periódicas.', ru: 'Вести реестр DPIA и планировать периодические пересмотры.' } }
  ),
];

// Breach Notification (Art.33-34)
const GDPR_BREACH: GdpControl[] = [
  def(
    'BR_01',
    'Breach Notification',
    { en: '72-hour regulator notification', es: 'Notificación al regulador en 72 horas', ru: 'Уведомление регулятора в течение 72 часов' },
    { en: 'Process to notify supervisory authority within 72 hours of breach awareness.', es: 'Proceso para notificar a la autoridad en 72 horas.', ru: 'Процесс уведомления надзорного органа в течение 72 часов.' },
    'CRITICAL',
    [ev('procedure', ['Breach notification procedure', '72h checklist'], ['Procedimiento de notificación', 'Lista 72h'], ['Процедура уведомления', 'Чек-лист 72ч'])],
    false,
    { CRITICAL: { en: 'Establish breach notification procedure with 72-hour capability.', es: 'Establecer procedimiento de notificación con capacidad 72 horas.', ru: 'Установить процедуру уведомления с возможностью в течение 72 часов.' } }
  ),
  def(
    'BR_02',
    'Breach Notification',
    { en: 'Data subject breach notification', es: 'Notificación a interesados', ru: 'Уведомление субъектов данных' },
    { en: 'Data subjects are notified when breach poses high risk to their rights.', es: 'Los interesados son notificados cuando el riesgo es alto.', ru: 'Субъекты данных уведомляются при высоком риске для их прав.' },
    'CRITICAL',
    [ev('procedure', ['Data subject notification procedure'], ['Procedimiento de notificación a interesados'], ['Процедура уведомления субъектов'])],
    false,
    { CRITICAL: { en: 'Define procedure for notifying data subjects of breaches.', es: 'Definir procedimiento para notificar a interesados de brechas.', ru: 'Определить процедуру уведомления субъектов об утечках.' } }
  ),
];

// Processors & Supply Chain (Art.28)
const GDPR_PROCESSORS: GdpControl[] = [
  def(
    'PROC_01',
    'Processors & Supply Chain',
    { en: 'Processor agreements (DPA)', es: 'Acuerdos con encargados (DPA)', ru: 'Соглашения с обработчиками (DPA)' },
    { en: 'Data Processing Agreements in place with all processors.', es: 'Acuerdos de Procesamiento de Datos con todos los encargados.', ru: 'Соглашения об обработке данных со всеми обработчиками.' },
    'CRITICAL',
    [ev('contract', ['DPA template', 'Signed DPAs'], ['Plantilla DPA', 'DPAs firmados'], ['Шаблон DPA', 'Подписанные DPA'])],
    true,
    { CRITICAL: { en: 'Conclude DPAs with all processors; include Art.28 requirements.', es: 'Celebrar DPAs con todos los encargados; incluir requisitos Art.28.', ru: 'Заключить DPA со всеми обработчиками; включить требования ст.28.' } },
    { ISO27001: ['A.5.19', 'A.5.23'] }
  ),
  def(
    'PROC_02',
    'Processors & Supply Chain',
    { en: 'Supplier security assessment', es: 'Evaluación de seguridad de proveedores', ru: 'Оценка безопасности поставщиков' },
    { en: 'Processors are assessed for security and compliance before engagement.', es: 'Los encargados se evalúan antes del compromiso.', ru: 'Обработчики оцениваются до начала сотрудничества.' },
    'MAJOR',
    [ev('record', ['Vendor assessment', 'Security questionnaire'], ['Evaluación de proveedores', 'Cuestionario de seguridad'], ['Оценка поставщиков', 'Анкета безопасности'])],
    true,
    { MAJOR: { en: 'Conduct security assessments of processors before onboarding.', es: 'Realizar evaluaciones de seguridad antes del onboarding.', ru: 'Проводить оценку безопасности обработчиков до начала работы.' } }
  ),
  def(
    'PROC_03',
    'Processors & Supply Chain',
    { en: 'International transfer safeguards', es: 'Salvaguardas para transferencias internacionales', ru: 'Меры при международном трансфере' },
    { en: 'SCC or adequacy decision for transfers outside EEA.', es: 'SCC o decisión de adecuación para transferencias fuera del EEE.', ru: 'SCC или решение об адекватности для трансферов вне ЕЭП.' },
    'CRITICAL',
    [ev('contract', ['SCC', 'Adequacy documentation'], ['SCC', 'Documentación de adecuación'], ['SCC', 'Документация об адекватности'])],
    true,
    { CRITICAL: { en: 'Implement SCC or adequacy for international data transfers.', es: 'Implementar SCC o adecuación para transferencias internacionales.', ru: 'Внедрить SCC или адекватность для международных трансферов.' } }
  ),
];

// Accountability & Governance (Art.5(2), 24, 30, 37)
const GDPR_ACCOUNTABILITY: GdpControl[] = [
  def(
    'ACC_01',
    'Accountability & Governance',
    { en: 'Records of processing activities (Art.30)', es: 'Registro de actividades (Art.30)', ru: 'Реестр операций (ст.30)' },
    { en: 'RoPA maintained with all required information.', es: 'RoPA mantenido con toda la información requerida.', ru: 'Ведётся RoPA со всей требуемой информацией.' },
    'CRITICAL',
    [ev('record', ['RoPA', 'Art.30 register'], ['RoPA', 'Registro Art.30'], ['RoPA', 'Реестр ст.30'])],
    false,
    { CRITICAL: { en: 'Maintain Records of Processing Activities per Art.30.', es: 'Mantener Registro de Actividades de Procesamiento según Art.30.', ru: 'Вести реестр операций обработки в соответствии со ст.30.' } }
  ),
  def(
    'ACC_02',
    'Accountability & Governance',
    { en: 'DPO designation and role', es: 'Designación y rol del DPO', ru: 'Назначение и роль DPO' },
    { en: 'DPO appointed where required; contact published; independence ensured.', es: 'DPO designado si es requerido; contacto publicado; independencia asegurada.', ru: 'DPO назначен при необходимости; контакт опубликован; обеспечена независимость.' },
    'CRITICAL',
    [ev('record', ['DPO appointment', 'Contact publication'], ['Designación DPO', 'Publicación de contacto'], ['Назначение DPO', 'Публикация контакта'])],
    false,
    { CRITICAL: { en: 'Appoint DPO where required and publish contact details.', es: 'Designar DPO cuando sea requerido y publicar datos de contacto.', ru: 'Назначить DPO при необходимости и опубликовать контактные данные.' } }
  ),
  def(
    'ACC_03',
    'Accountability & Governance',
    { en: 'Accountability and documentation', es: 'Rendición de cuentas y documentación', ru: 'Подотчётность и документирование' },
    { en: 'Policies and procedures demonstrate accountability for compliance.', es: 'Políticas y procedimientos demuestran rendición de cuentas.', ru: 'Политики и процедуры демонстрируют подотчётность.' },
    'MAJOR',
    [ev('policy', ['Accountability policy', 'Compliance documentation'], ['Política de rendición', 'Documentación de cumplimiento'], ['Политика подотчётности', 'Документация соответствия'])],
    false,
    { MAJOR: { en: 'Document and maintain accountability evidence.', es: 'Documentar y mantener evidencia de rendición de cuentas.', ru: 'Документировать и поддерживать доказательства подотчётности.' } }
  ),
];

export const GDPR_CONTROLS_CATALOG: GdpControl[] = [
  ...GDPR_LAWFULNESS,
  ...GDPR_RIGHTS,
  ...GDPR_SECURITY,
  ...GDPR_DPIA,
  ...GDPR_BREACH,
  ...GDPR_PROCESSORS,
  ...GDPR_ACCOUNTABILITY,
];

const DOMAIN_ORDER: GdpDomain[] = [
  'Lawfulness & Transparency',
  'Data Subject Rights',
  'Security of Processing',
  'DPIA & Risk',
  'Breach Notification',
  'Processors & Supply Chain',
  'Accountability & Governance',
];

export function getGdpControlsByDomain(domain: GdpDomain): GdpControl[] {
  return GDPR_CONTROLS_CATALOG.filter((c) => c.domain === domain);
}

export function getGdpControlByCode(code: string): GdpControl | undefined {
  return GDPR_CONTROLS_CATALOG.find(
    (c) => c.controlCode === code || c.controlCode === `GDPR_${code}`
  );
}

export function getGdpControlDomains(): GdpDomain[] {
  return DOMAIN_ORDER;
}
